import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Play } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import SiteContentText from './SiteContentText';
import { usePublicBookingStats } from '../hooks/usePublicBookingStats';
import { HERO_TRUST_LINE_DEFAULT } from '../utils/publicBookingStats';

const Hero: React.FC = () => {
  const [content, setContent] = useState({
    title: '',
    subtitle: '',
    button1_text: '',
    button2_text: '',
    trust_line: '',
    hero_video_url: '',
  });
  const [loading, setLoading] = useState(true);
  const { display: bookingProof, loading: bookingStatsLoading } = usePublicBookingStats();

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const { data, error } = await supabase
        .from('site_content')
        .select('key, content')
        .eq('category', 'hero');

      if (error) throw error;

      const contentMap = data?.reduce((acc, item) => {
        acc[item.key] = item.content;
        return acc;
      }, {} as Record<string, string>) ?? {};

      setContent({
        title: contentMap.hero_title ?? 'PROFFSIG FESTKÄNSLA, UTAN PROFFSPRIS!',
        subtitle: contentMap.hero_subtitle ?? 'Skånes modernaste uthyrningstjänst för fest och event. Vi gör det enkelt att hyra professionella högtalare, PA och ljusutrustning för student, bröllop och företagsevent.',
        button1_text: contentMap.hero_button1_text ?? 'Se våra paket',
        button2_text: contentMap.hero_button2_text ?? 'Kontakta oss',
        trust_line: contentMap.hero_trust_line ?? HERO_TRUST_LINE_DEFAULT,
        hero_video_url: contentMap.hero_video_url ?? '',
      });
    } catch (err) {
      console.error('Error fetching hero content:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="relative min-h-screen bg-slate-900 overflow-hidden -mt-[var(--header-height,7rem)] pt-[var(--header-height,7rem)] flex items-center"
      aria-label="Hero"
    >
      <div className="absolute inset-0 z-0">
        <video
          key={content.hero_video_url || 'hero-bg'}
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          aria-hidden
        >
          <source
            src={content.hero_video_url || '/hero-bg.mp4'}
            type={content.hero_video_url?.endsWith('.webm') ? 'video/webm' : 'video/mp4'}
          />
        </video>
        {/* Stronger overlay for text contrast against video */}
        <div className="absolute inset-0 bg-slate-950/65" aria-hidden />
        <div
          className="absolute inset-0 bg-gradient-to-b from-slate-950/40 via-transparent to-slate-950/50"
          aria-hidden
        />
      </div>

      <div className="relative z-10 container mx-auto px-4 py-16 md:py-28 pb-16 md:pb-36">
        <div className="max-w-3xl mx-auto text-center">
          {loading ? (
            <div className="animate-pulse space-y-6">
              <div className="h-12 md:h-14 bg-slate-700 rounded w-4/5 mx-auto" />
              <div className="h-6 bg-slate-700 rounded w-full max-w-xl mx-auto" />
              <div className="h-6 bg-slate-700 rounded w-2/3 mx-auto" />
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <div className="h-12 bg-slate-700 rounded w-40 mx-auto sm:mx-0" />
                <div className="h-12 bg-slate-700 rounded w-40 mx-auto sm:mx-0" />
              </div>
            </div>
          ) : (
            <>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4 drop-shadow-[0_2px_8px_rgba(0,0,0,0.65)]">
                {content.title}
              </h1>
              <SiteContentText
                as="p"
                className="text-lg md:text-xl text-slate-100 mb-8 max-w-2xl mx-auto leading-relaxed drop-shadow-[0_1px_4px_rgba(0,0,0,0.55)]"
                text={content.subtitle}
              />
              <div className="flex flex-col sm:flex-row gap-3 justify-center mb-10">
                <a
                  href="#populara-paket"
                  className="inline-flex items-center justify-center bg-purple-600 text-white font-medium py-3 px-6 rounded-none hover:bg-purple-700 transition-colors"
                >
                  <span className="mr-2">{content.button1_text}</span>
                  <ArrowRight size={18} />
                </a>
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center border border-white/40 text-white font-medium py-3 px-6 rounded-none hover:bg-white/10 transition-colors"
                >
                  <Play className="mr-2" size={18} />
                  {content.button2_text}
                </Link>
              </div>
              {content.trust_line && (
                <div
                  className="max-w-xl mx-auto bg-slate-950/80 border border-white/25 shadow-[0_0_0_1px_rgba(255,255,255,0.06),0_12px_40px_rgba(0,0,0,0.45)] backdrop-blur-md rounded-none px-5 py-3.5 md:px-6 md:py-4"
                  aria-live="polite"
                >
                  <SiteContentText
                    as="p"
                    className="text-sm md:text-base text-white font-medium leading-snug"
                    text={content.trust_line}
                  />
                  <p
                    className={`mt-1.5 text-sm md:text-base leading-snug min-h-[1.5rem] ${
                      bookingProof
                        ? 'text-white'
                        : bookingStatsLoading
                          ? 'text-transparent'
                          : 'hidden'
                    }`}
                  >
                    {bookingProof ? (
                      <>
                        {bookingProof.mode === 'previous_year' ? (
                          <>
                            Under {bookingProof.year} fick vi{' '}
                            <span className="font-semibold tabular-nums text-white">
                              {bookingProof.count}
                            </span>{' '}
                            bokningar.
                          </>
                        ) : (
                          <>
                            Hittills i år har vi fått{' '}
                            <span className="font-semibold tabular-nums text-white">
                              {bookingProof.count}
                            </span>{' '}
                            bokningar.
                          </>
                        )}
                      </>
                    ) : (
                      <span aria-hidden>Hittills i år har vi fått 000 bokningar.</span>
                    )}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default Hero;
